import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import api from "../../services/api";
import { getCurrentUser, setCurrentUser } from "../../utils/permissions";

export default function AuthPermissionSync({ children }) {
  const mountedRef = useRef(false);
  const [ready, setReady] = useState(false);
  const [authenticationFailed, setAuthenticationFailed] = useState(false);

  const refreshUser = useCallback(async ({ initial = false } = {}) => {
    try {
      const response = await api.get("/auth/me");
      const freshUser = response.data?.user;

      if (!freshUser) {
        throw new Error("The server did not return the current user");
      }

      setCurrentUser(freshUser);

      if (mountedRef.current) {
        setAuthenticationFailed(false);
      }
    } catch (error) {
      const status = error.response?.status;

      if (status === 401 || status === 403 || status === 404) {
        localStorage.removeItem("token");
        setCurrentUser(null);

        if (mountedRef.current) {
          setAuthenticationFailed(true);
        }
      } else {
        console.error("AUTH PERMISSION SYNC ERROR:", error);

        // A temporary network/server problem should not immediately log out a
        // user who still has a valid cached session.
        if (initial && !getCurrentUser() && mountedRef.current) {
          setAuthenticationFailed(true);
        }
      }
    } finally {
      if (initial && mountedRef.current) {
        setReady(true);
      }
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    refreshUser({ initial: true });

    const handleWindowFocus = () => {
      refreshUser();
    };

    window.addEventListener("focus", handleWindowFocus);

    return () => {
      mountedRef.current = false;
      window.removeEventListener("focus", handleWindowFocus);
    };
  }, [refreshUser]);

  if (!ready) {
    return (
      <div className="grid min-h-screen place-items-center bg-slate-50 dark:bg-slate-950">
        <div className="text-center">
          <div className="mx-auto h-9 w-9 animate-spin rounded-full border-4 border-slate-200 border-t-blue-600" />
          <p className="mt-4 text-sm font-medium text-slate-500 dark:text-slate-400">
            Loading your access permissions...
          </p>
        </div>
      </div>
    );
  }

  if (authenticationFailed) {
    return <Navigate to="/login" replace />;
  }

  return children;
}
